'use client'

import { Button } from './ui/button'
import { Checkbox } from './ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormTextarea,
  LargeFormInput,
} from './ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import dynamic from 'next/dynamic'
import { usePostHog } from 'posthog-js/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { cn } from 'src/utils'
import { contact } from '~/app/actions'
import { ContactFormSchema, contactFormSchema } from '~/utils/forms'

const FormSuccess = dynamic(() => import('./form-success'))

const items = [
  {
    label: 'New Business',
  },
  {
    label: 'Careers',
  },
  {
    label: 'Press',
  },
  {
    label: 'Support',
  },
  {
    label: 'General',
  },
] as const

export function ContactForm() {
  const event = usePostHog()
  const [success, setSuccess] = useState(false)
  const form = useForm<ContactFormSchema>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      reason: items[4].label,
      message: '',
    },
  })

  async function onSubmit(values: ContactFormSchema) {
    try {
      event.capture('submitted contact form', values)
      await contact(values)
      form.reset()
      setSuccess(true)
    } catch (e) {
      event.capture('error submitting contact form', values)
    }
  }
  return (
    <>
      <FormSuccess open={success} onOpenChange={setSuccess} />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <section className="grid-container gap-16">
            <div className="lg:col-start-4 col-span-6 space-y-6">
              <h3 className="mb-6">Who do we have the pleasure of speaking with?</h3>
              <div className="flex justify-between space-x-4">
                <LargeFormInput
                  name="firstName"
                  label="First Name"
                  control={form.control}
                />
                <LargeFormInput
                  name="lastName"
                  label="Last Name"
                  control={form.control}
                />
              </div>
              <LargeFormInput
                name="email"
                label="Work Email"
                type="email"
                control={form.control}
              />
              <LargeFormInput
                name="phone"
                label="Phone"
                type="phone"
                control={form.control}
              />
              <LargeFormInput name="company" label="Company" control={form.control} />
            </div>
            <div className="lg:col-start-4 col-span-6 space-y-6">
              <FormField
                control={form.control}
                name="reason"
                render={() => (
                  <FormItem>
                    <div className="mb-6">
                      <h3 className="mb-6">What brings you here?</h3>
                    </div>
                    {items.map((item) => (
                      <FormField
                        key={item.label}
                        control={form.control}
                        name="reason"
                        render={({ field }) => {
                          return (
                            <label key={item.label} className="cursor-pointer">
                              <FormItem
                                className={cn(
                                  'flex flex-row w-auto items-center space-x-3 px-3 py-2 mb-1 bg-white rounded-md border-2 border-transparent cursor-pointer',
                                  {
                                    'border-secondary-black': field.value?.includes(
                                      item.label,
                                    ),
                                  },
                                )}
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={field.value == item.label}
                                    onCheckedChange={() => {
                                      return field.onChange(item.label)
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal cursor-pointer">
                                  {item.label}
                                </FormLabel>
                              </FormItem>
                            </label>
                          )
                        }}
                      />
                    ))}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormTextarea
                name="message"
                control={form.control}
                label="Drop the deets; leave us a message below"
              />
              <div className="h-10" />
              <Button
                disabled={form.formState.isSubmitting}
                type="submit"
                variant="primary"
                arrow
                rounded
                className="w-full"
              >
                Submit
              </Button>
            </div>
          </section>
        </form>
      </Form>
    </>
  )
}
